import React, { useContext, useEffect, useState } from "react";
import { FormContext } from "../form_buttons";
import { Button, ButtonGroup, InputLabel } from "@mui/material";
import { TypeConf, useType } from "../../types";
import { Box } from "@mui/system";
import { fieldMarginBottom } from "../../form_constants";

interface FormSelectButtonsProp<T> {
    field: string;
    label: string;
    type: TypeConf<T>;
    multiple?: boolean;
    items?: T[];
    color?: "inherit" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
    size?: "small" | "medium" | "large";
    onChange?: (value: T | T[] | undefined) => void;
}

function FormSelectButtons<T>(props: FormSelectButtonsProp<T>) {
    const { field, label, type, multiple, items, color, size, onChange } = props;

    const typer = useType(type, multiple);
    const typeManager = typer.typeManager;

    const [elements, setElements] = useState<any[]>(items || []);
    const formContext = useContext(FormContext);
    const model = formContext.form;

    const modelField = model.field(field);
    const [selectedItems, setSelectedItems] = useState<any[]>(
        (modelField.valueAsItem(true) as any[]) || []
    );

    model.register(
        field,
        () => typeManager.finalValue(selectedItems),
        incoming => {
            if (!incoming) {
                setSelectedItems([]);
            } else if (Array.isArray(incoming)) {
                setSelectedItems(incoming);
            } else {
                setSelectedItems([incoming]);
            }
        },
    );

    useEffect(() => {
        if (!items) {
            typer.supplier().then(array => setElements(array));
        }
    }, [type]);

    const handleSelect = (item: T) => {
        const newSelection = typeManager.select(selectedItems, item);
        setSelectedItems(newSelection);
        const finalValue = typeManager.finalValue(newSelection);
        model.notifyFieldChange(field, finalValue);
        onChange?.(finalValue as T | T[] | undefined);
    };

    return (
        <Box className={["form-input", "form-input-" + field].join(" ")} mb={fieldMarginBottom}>
            {elements && (
                <Box mb={4}>
                    <InputLabel id={field + "-lbl"} style={{ paddingBottom: "7px" }}>
                        {label}
                    </InputLabel>
                    <ButtonGroup variant="contained" color={color} aria-label={label} fullWidth>
                        {elements.length > 0 &&
                            elements.map((item, index) => (
                                <Button
                                    size={size}
                                    key={"btn-group-" + field + "-btn-" + index}
                                    fullWidth
                                    color={typeManager.selected(selectedItems, item) ? color : "inherit"}
                                    onClick={() => handleSelect(item)}>
                                    <Box
                                        sx={{
                                            p: 0,
                                            textOverflow: "ellipsis",
                                            width: "100%",
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                        }}
                                        title={type.labeler(item)}>
                                        <Box sx={{ display: { xs: "inline", sm: "none" } }}>{index}</Box>{" "}
                                        {type.labeler(item)}
                                    </Box>
                                </Button>
                            ))}
                    </ButtonGroup>
                </Box>
            )}
        </Box>
    );
}

export default FormSelectButtons;
